import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  MinusIcon,
  TicketIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { ClubResponse } from "*";
import dayjs from "dayjs";
import snippets from "../i18n/lang";
import { useDateRange } from "../hooks";
import { classNames } from "../utils/classNames";
import formatNumber from "../utils/formatNumber";
import { getSign } from "../utils/getSign";
import {
  daypassChange,
  daypassChartPoints,
  daypassPerBuyer,
  daypassTemplateRows,
} from "../utils/daypassStats";
import { Chart } from "./Chartjs";
import { ClubStat } from "./ClubList";
import { SubHeadline } from "./Headline";
import { StatsCard } from "./StatCard";

/** primary-400 — the daypass bars sit a shade lighter than the checkins below them. */
const BAR_COLOR = "#86C2EB";

/**
 * Daypass sales of one club. The block is served only for clubs that offer a daypass, so its
 * absence is the normal case and renders nothing at all.
 */
export default function Daypass({ club }: { club: ClubResponse }) {
  const { interval } = useDateRange();
  const daypass = club?.daypass;

  if (!daypass) return null;

  const labelFormat =
    interval === "month" ? "DD.MM.YY" : interval === "year" ? "MMM." : "dd";
  const { labels, visits } = daypassChartPoints(daypass);
  const change = daypassChange(daypass);
  const perBuyer = daypassPerBuyer(daypass);
  const rows = daypassTemplateRows(daypass);
  // Without a comparison there is no trend to point at, so the neutral daypass icon stands in.
  const TrendIcon =
    change === null
      ? TicketIcon
      : change > 0
      ? ArrowTrendingUpIcon
      : change < 0
      ? ArrowTrendingDownIcon
      : MinusIcon;

  return (
    <div className="my-8">
      <SubHeadline className="mb-3">
        {snippets.daypassDetail.headline}
      </SubHeadline>

      <StatsCard.Plain
        tooltip={snippets.daypassDetail.tooltip}
        backgroundColor="bg-primary-500 text-white"
      >
        <div className="grid grid-cols-2 w-full">
          <div>
            <span className="text-sm">{snippets.daypassDetail.sold}</span>
            <div className="text-xl mt-1 flex items-center gap-3">
              <TrendIcon className="w-5 h-5" />
              {formatNumber(daypass.sold)}
              {change !== null && (
                <small
                  className={classNames(
                    "text-xs",
                    change < 0 ? "text-gray-300" : "text-primary-400"
                  )}
                >
                  {getSign(change)}
                  {formatNumber(change)}%
                </small>
              )}
            </div>
          </div>
          <div>
            <span className="text-sm">{snippets.daypassDetail.perBuyer}</span>
            <div className="text-xl mt-1 flex items-center gap-3">
              <UserIcon className="w-5 h-5" />
              {perBuyer === null ? "–" : formatNumber(perBuyer)}
            </div>
          </div>
        </div>
      </StatsCard.Plain>

      <div className="mt-6">
        <div className="text-sm font-semibold text-primary-500 mb-2">
          {snippets.daypassDetail.chartTitle}
        </div>
        <Chart
          type="bar"
          options={{
            scales: {
              y: {
                beginAtZero: true,
                grid: { display: false },
                ticks: { precision: 0 },
              },
              x: {
                grid: { display: false },
                ticks: { autoSkip: true, maxTicksLimit: 8 },
              },
            },
            datasets: {
              bar: {
                borderRadius: 100,
                borderSkipped: false,
              },
            },
            plugins: { legend: { display: false } },
          }}
          data={{
            labels: labels.map((label) => dayjs(label).format(labelFormat)),
            datasets: [
              {
                label: snippets.daypassDetail.chartTitle,
                data: visits,
                backgroundColor: BAR_COLOR,
              },
            ],
          }}
        />
      </div>

      {rows.length > 0 && (
        <div className="mt-6">
          <div className="text-sm font-semibold text-primary-500 mb-2">
            {snippets.daypassDetail.perTemplate}
          </div>
          {rows.map((row) => (
            <DaypassCard key={row.key} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

const DaypassCard = ({
  row,
}: {
  row: ReturnType<typeof daypassTemplateRows>[number];
}) => (
  <div className="mb-1 rounded-xl bg-gray-100 p-3">
    <div className="text-gray-500 text-sm font-normal mb-2 truncate">
      {row.name || "unbekannt"}
    </div>

    <div className="flex items-end justify-between gap-2 mb-3">
      <div className="max-[330px]:hidden self-center">
        <TicketIcon className="text-primary-500 w-6 h-6 sm:w-8 sm:h-8" />
      </div>
      <ClubStat
        large
        signValue={false}
        name={snippets.daypassDetail.soldShort}
        value={row.sold}
      />
      <div className="flex flex-col w-full items-start grow-0">
        <strong className="text-xl font-medium text-gray-600 opacity-55">
          {formatNumber(Number(row.share.toFixed(1)))} %
        </strong>
        <div className="text-gray-400 text-[12px] leading-3">
          {snippets.daypassDetail.share}
        </div>
      </div>
    </div>

    {row.sold > 0 && (
      <div className="w-full flex text-white rounded-full overflow-hidden text-[10px] bg-gray-300">
        <div
          className="bg-primary-500 min-w-min text-ellipsis px-2 py-1 rounded-full overflow-hidden"
          style={{ width: `${Math.max(Math.ceil(row.share), 8)}%` }}
        >
          {row.sold}
        </div>
      </div>
    )}
  </div>
);
